import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import CalendarPicker from './components/CalendarPicker';

export default function Booking() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      setError(t('booking.calendarRequired'));
      return;
    }
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncoded.append(key, value.toString());
    });

    // Append selected date and time
    urlEncoded.append('booking_date', selectedDate);
    urlEncoded.append('booking_time', selectedTime);

    try {
      const res = await fetch('https://readdy.ai/api/form/d8ofq5p8gb8kvsd0o9ng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        setSelectedDate(null);
        setSelectedTime(null);
      } else {
        setError(t('booking.error'));
      }
    } catch {
      setError(t('booking.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  const calendarRequired = !selectedDate || !selectedTime;

  return (
    <Layout>
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-primary-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent-500 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent-400 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-300 mb-4">
            {t('booking.tagline')}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {t('bookingPage.heroTitle')}
          </h1>
          <p className="text-base text-foreground-200 max-w-[600px] mx-auto leading-relaxed">
            {t('bookingPage.heroSubtitle')}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-background-50">
        <div className="max-w-[680px] mx-auto">
          {submitted ? (
            <div className="bg-accent-100 border border-accent-200 rounded-2xl p-10 text-center">
              <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-500 text-white">
                <i className="ri-check-line text-2xl" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
                {t('booking.successTitle')}
              </h2>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {t('booking.successMessage')}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Calendar Picker */}
              <CalendarPicker
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onDateChange={setSelectedDate}
                onTimeChange={setSelectedTime}
              />

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 md:p-10 border border-background-200/70">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">
                  {t('booking.formHeading')}
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-8">
                  {t('booking.formDescription')}
                </p>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="booking-form"
                  className="space-y-5"
                >
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                      {t('booking.labelName')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                      placeholder={t('booking.placeholderName')}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                      {t('booking.labelEmail')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                      placeholder={t('booking.placeholderEmail')}
                    />
                  </div>

                  <div>
                    <label htmlFor="area" className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                      {t('booking.labelArea')}
                    </label>
                    <select
                      id="area"
                      name="area"
                      required
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors cursor-pointer"
                    >
                      <option value="">{t('booking.placeholderArea')}</option>
                      <option value="supply-chain">{t('booking.option.supply-chain')}</option>
                      <option value="career-change">{t('booking.option.career-change')}</option>
                      <option value="young-professional">{t('booking.option.young-professional')}</option>
                      <option value="interview-prep">{t('booking.option.interview-prep')}</option>
                      <option value="leadership">{t('booking.option.leadership')}</option>
                      <option value="other">{t('booking.option.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                      {t('booking.labelMessage')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors resize-none"
                      placeholder={t('booking.placeholderMessage')}
                    />
                    <p className="text-xs text-foreground-400 mt-1">{t('booking.maxChars')}</p>
                  </div>

                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full px-6 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 disabled:opacity-50 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    {submitting ? t('booking.sending') : t('booking.submitButton')}
                    <i className="ri-arrow-right-line" />
                  </button>
                </form>

                {/* What Happens Next — Compact */}
                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <h3 className="font-heading text-sm font-bold text-foreground-950 mb-5 text-center uppercase tracking-wider">
                    {t('booking.whatHappensTitle')}
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-heading text-sm font-bold">1</div>
                      <p className="text-xs font-medium text-foreground-900">{t('booking.step1.title')}</p>
                    </div>
                    <div className="text-center">
                      <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-heading text-sm font-bold">2</div>
                      <p className="text-xs font-medium text-foreground-900">{t('booking.step2.title')}</p>
                    </div>
                    <div className="text-center">
                      <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-heading text-sm font-bold">3</div>
                      <p className="text-xs font-medium text-foreground-900">{t('booking.step3.title')}</p>
                    </div>
                  </div>                </div>
              </div>

              {/* Mini FAQ */}
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200/70">
                <div className="space-y-2">
                  <details className="group border border-background-200/70 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                      <span className="text-sm font-semibold text-foreground-900">{t('booking.faq1.question')}</span>
                      <i className="ri-add-line text-foreground-400 group-open:rotate-45 transition-transform duration-200" />
                    </summary>
                    <div className="px-4 pb-3">
                      <p className="text-sm text-foreground-600 leading-relaxed">{t('booking.faq1.answer')}</p>
                    </div>
                  </details>
                  <details className="group border border-background-200/70 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                      <span className="text-sm font-semibold text-foreground-900">{t('booking.faq2.question')}</span>
                      <i className="ri-add-line text-foreground-400 group-open:rotate-45 transition-transform duration-200" />
                    </summary>
                    <div className="px-4 pb-3">
                      <p className="text-sm text-foreground-600 leading-relaxed">{t('booking.faq2.answer')}</p>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}